import _ from "lodash";
import { AbstractSchemaConfigWithGlobalRelationOptions } from "../schemas/manageSchemasAPI";

export type AttributeType =
    | "boolean"
    | "string"
    | "number"
    | "date"
    | "datetime"
    | "string_array"
    | "number_array"
    | "entry_relation"
    | "global_relation"
    | "numeric_w_units";

export type AttributeBase = {
    name: string;
    automated?: boolean;
    display_name?: string;
    display_order?: number;
    editable?: boolean;
    field_type?: string;
    hidden?: boolean;
    option_default?: string | null;
    options?: string[] | null;
    /**
     * value_default is currently used for the default value of a numeric attribute.
     * For defaulting the value of other attribute types, option_default is used.
     */
    value_default?: string | null;
    relation?: string;
    required?: boolean;
    type?: AttributeType;
    allow_multiple_values?: boolean;
    widget_type?: string;
    metadata?: boolean;
    /**
     * A "subordinate" attribute is one that depends on another attribute for its
     * configuration. The `_w_units` attributes create a `_units` subordinate
     * attribute that captures and represents the units of a value.
     */
    subordinate_to_attribute?: string;
    source?: "protocol" | "template" | "overridden";
};

export type ValueAttribute = AttributeBase & {
    type: "boolean" | "string" | "number" | "date" | "datetime" | "string_array" | "number_array";
};

export type ThingValue = boolean | string | number | Date | string[] | number[];

export type RelationType = "entry_relation" | "global_relation";

export type RelationAttribute = AttributeBase & {
    type: RelationType;
    name: string;
    relation: string;
};

export type AttributeOT = AttributeBase & {
    name: string;
    default?: string;
    max?: number;
    min?: number;
};

export type Attribute = AttributeBase | ValueAttribute | RelationAttribute | AttributeOT;

export type RelationOptions = {
    relation: string;
    options: RelationOption[];
};

export type RelationOption = {
    value: string;
    displayName: string;
};

export type SetupPerConfigObject = {
    [key: string]: number | string;
};

export type SetupPerConfig = {
    attributeName: string;
    value: string | number;
};

export type SchemaSetup = {
    naming_template?: string;
    per?: Record<string, unknown>;
};

export type ThingSchema = {
    id: string;
    name: string;
    oneTimeAttributes: Record<string, Attribute>;
    timeSeriesAttributes: Record<string, Attribute>;
    parentThingSchemaName: string;
    protocolCode: string | null;
    schemaConfigId: string | null;
    canAddInstanceInMango: boolean;
    displayName: string;
    isBenchling: boolean;
    isGeotaggable: boolean;
    hasWellPosition: boolean;
    isParentBenchling: boolean;
    createdAt: Date;
    updatedAt: Date;
    setup: SchemaSetup | null;
};

export type Protocol = AbstractSchemaConfigWithGlobalRelationOptions & {
    code: string;
    name: string;
    protocolType: string;
    subwellOutputs: object | null;
    schedule: string | null;
    projectId: string;
    sourceId: string;
    schema: string;
    url: string;
    displayPlate: boolean;
    functionalTeam: string | null;
    isMicrobial: boolean;
};

export type Experiment = {
    code: string;
    name: string;
    displayName: string;
    alternativeIdentifier: string | null;
    siteTimeZone: string;
    day0: string;
    operator: string[];
    investigator: string | null;
    entryRelationOptions: RelationOptions[];
    conditions: Condition[];
    isUsingMeasurements: boolean;
    benchlingEntryId: string;
    url: string;
    experimentGroup: string | null;
};

export type BasicThing = {
    id: string;
    displayName: string;
    thingSchemaId: string;
    benchlingSchema: string | null;
    parentThingId: string | null;
    benchlingParentId: string | null;
    entry: string;
    isBenchling: boolean;
    protocol: string;
    pin: Pin | null;
    bearing: number | null;
    tilt: number | null;
    zoom: number | null;
    capturedBy: string | null;
    createdAt: Date;
};

/**
 * DTO for a Thing object, used for APIs.
 */
export type Thing = BasicThing & {
    condition: Condition | null;
    parentThingDisplayName: string | null;
    containerWellPosition: ContainerWellPosition | null;
};

export type Pin = {
    x: number;
    y: number;
};

export type Condition = {
    id: string | null;
    name: string | null;
    number: string | number | null;
};

export type ContainerWellPosition = {
    wellPosition: string;
    containerName: string;
};

export function getRelationOptionDisplayName(
    relationOptions: RelationOptions[],
    attribute: Attribute,
    measurementValue: unknown
): unknown {
    const relationOption = getRelationOptionForValue(relationOptions, attribute, measurementValue);

    // TODO: if the measurement value doesn't map to a valid option value, what should we do?
    return relationOption?.displayName || measurementValue;
}

/**
 * Returns the RelationOption associated to the attribute and input value, matching both
 * "display" value or "id" value.
 */
export function getRelationOptionForValue(
    relationOptions: RelationOptions[],
    attribute: Attribute,
    measurementValue: unknown
): RelationOption | null {
    const relationName = attribute.relation!;
    const globalRelationOption = findRelationOptionForRelation(relationName, relationOptions);

    return findRelationOptionValueForInputValue(globalRelationOption, measurementValue as string);
}

/**
 * Returns the RelationOption for the given input value, which may be either the "display"
 * value or the "id" value.
 */
export function findRelationOptionValueForInputValue(
    relationOptions: RelationOptions | null,
    inputValue: string
): RelationOption | null {
    if (_.isNil(relationOptions)) {
        return null;
    }

    return (
        relationOptions.options.find((option) => {
            return option.displayName === inputValue || option.value === inputValue;
        }) || null
    );
}

export function findRelationOptionForRelation(
    relationName: string,
    relationOptions: RelationOptions[]
) {
    const found = relationOptions.find((option) => option.relation === relationName);
    return found || null;
}
